import useSWR from "swr";
import { cfApi, cfFetch, apiClient } from "../api/apiClient";
import type { BankReconciliationData } from "../types/bankReconciliation";

const RECONCILIATION_BASE = cfApi("/bank-reconciliation");

function reconciliationKey(uploadBatchId: string | null) {
  if (!uploadBatchId) return RECONCILIATION_BASE;
  const params = new URLSearchParams({ uploadBatchId });
  return `${RECONCILIATION_BASE}?${params.toString()}`;
}

function parseApiError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "error" in detail) {
    const message = (detail as { error?: unknown }).error;
    if (typeof message === "string") return message;
  }
  if (detail && typeof detail === "object" && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "Request failed";
}

async function reconciliationFetcher(url: string) {
  const response = await cfFetch(url);
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(
      typeof (payload as { message?: string })?.message === "string"
        ? (payload as { message: string }).message
        : "Failed to load bank reconciliation data",
    );
  }
  return payload as BankReconciliationData;
}

export interface MatchPair {
  bankTransactionId: string;
  journalEntryId: string;
}

export interface BulkMatchResult {
  succeeded: number;
  failed: number;
  firstError: string | null;
}

export function useBankReconciliation(uploadBatchId: string | null = null) {
  const { data, error, isLoading, isValidating, mutate } =
    useSWR<BankReconciliationData>(
      reconciliationKey(uploadBatchId),
      reconciliationFetcher,
      { keepPreviousData: true },
    );

  async function postMatch(pair: MatchPair): Promise<void> {
    try {
      await apiClient.post("/bank-reconciliation/matches", pair);
    } catch (error) {
      throw new Error(parseApiError(error));
    }
  }

  async function createMatch(
    bankTransactionId: string,
    journalEntryId: string,
  ): Promise<void> {
    await postMatch({ bankTransactionId, journalEntryId });
    await mutate();
  }

  /** Accept many matches sequentially, refetching once at the end. */
  async function createMatches(
    pairs: MatchPair[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<BulkMatchResult> {
    let succeeded = 0;
    let failed = 0;
    let firstError: string | null = null;
    for (const [index, pair] of pairs.entries()) {
      try {
        await postMatch(pair);
        succeeded += 1;
      } catch (error) {
        failed += 1;
        if (firstError === null) {
          firstError = error instanceof Error ? error.message : "Request failed";
        }
      }
      onProgress?.(index + 1, pairs.length);
    }
    await mutate();
    return { succeeded, failed, firstError };
  }

  async function deleteMatch(matchId: string): Promise<void> {
    try {
      await apiClient.delete(`/bank-reconciliation/matches/${matchId}`);
    } catch (error) {
      throw new Error(parseApiError(error));
    }
    await mutate();
  }

  /** Push reviewed matches to the connected accounting software. */
  async function pushToErp(matchIds: string[]): Promise<{
    pushed: string[];
    already_pushed: string[];
    not_found: string[];
  }> {
    let result;
    try {
      const response = await apiClient.post("/bank-reconciliation/erp-push", {
        matchIds,
      });
      result = response.data;
    } catch (error) {
      throw new Error(parseApiError(error));
    }
    await mutate();
    return result;
  }

  return {
    data,
    error,
    isLoading,
    isValidating,
    createMatch,
    createMatches,
    deleteMatch,
    pushToErp,
    refresh: mutate,
  };
}
